/**
 * Список контрагентів. КАРТКА — окрема ПОВНОЦІННА сторінка
 * /contractors/:id (рішення власника 2026-08-14; раніше — Drawer
 * поверх списку, див. ContractorCardPage).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';
import {
  contractors,
  type Contractor, type ContractorType, type ContractorRoleFilter,
} from '@/api/catalogs';
import { refs } from '@/api/refs';
import { usePermissions } from '@/auth/usePermissions';
import ImportedBadge from '@/shared/components/ImportedBadge';

export default function ContractorsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');  // '' = всі групи
  const [roleFilter, setRoleFilter] = useState<'' | ContractorRoleFilter>('');
  // 2026-07-24: ?new=1 — лінки «створити →» з редакторів документів.
  const [urlParams] = useSearchParams();
  useEffect(() => {
    if (urlParams.get('new')) navigate('/contractors/new', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TD-PAGINATION (2026-06-07): серверна пагінація; ч.691 — InfiniteDocGrid.
  const { datasource, total } = useInfiniteGridDatasource<Contractor>(
    (limit, offset, sort) => contractors.list({
      q: search || undefined, group_id: groupFilter || undefined,
      role: roleFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [search, groupFilter, roleFilter],
  );
  // ч.1094: кнопка «дублі (N)» — лише тим, хто редагує довідник.
  const { has } = usePermissions();
  const canEdit = has('refs.contractors.edit');
  const dupQ = useQuery({
    queryKey: ['contractor-duplicates'], queryFn: contractors.duplicates,
    enabled: canEdit, staleTime: 60_000,
  });
  const groupsQ = useQuery({
    queryKey: ['contractor-groups'],
    queryFn: refs.contractorGroups.list,
  });
  // Оновити список/бейджі при поверненні з картки (react-query invalidate
  // не працює для infinite datasource — але сторінка перезаходиться заново,
  // datasource створюється свіжий; кеш карантину чистимо).
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ['quarantine'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cols = useMemo<ColDef<Contractor>[]>(() => [
    {
      headerName: '',
      width: 110,
      sortable: false,
      cellRenderer: (p: { data?: Contractor }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ImportedBadge source={p.data?.external_source} />
          {p.data?.needs_review ? (
            <span
              title="Реквізити з 1С не відповідають стандарту. Відкрийте картку → виправте."
              style={{
                background: '#fde68a', color: 'var(--badge-amber-fg)', borderRadius: 4,
                padding: '2px 6px', fontSize: 12, fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              ⚠ Карантин
            </span>
          ) : null}
        </span>
      ),
    },
    { field: 'code', headerName: t('contractor.code'), width: 110, sortable: true },
    { field: 'name', headerName: t('contractor.name'), flex: 1, minWidth: 250, sortable: true },
    {
      field: 'contractor_type',
      headerName: t('contractor.type'),
      width: 170, sortable: true,
      valueFormatter: (p) => t(`contractor.types.${p.value as ContractorType}`),
    },
    { field: 'edrpou', headerName: t('contractor.edrpou'), width: 120, sortable: true },
    { field: 'inn', headerName: t('contractor.inn'), width: 120, sortable: true },
    {
      field: 'is_vat_payer',
      headerName: t('contractor.is_vat_payer'),
      width: 110, sortable: true,
      valueFormatter: (p) => (p.value ? t('common.yes') : t('common.no')),
    },
    {
      headerName: 'Роль',
      width: 200,
      valueGetter: (p) => {
        const c = p.data;
        if (!c) return '';
        const tags = [];
        if (c.is_buyer) tags.push('Покупець');
        if (c.is_supplier) tags.push('Постачальник');
        if (c.is_other) tags.push('Iнше');
        return tags.join(', ');
      },
    },
  ], [t]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {t('contractor.title')}
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
              ({total})
            </span>
          )}
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          {/* ч.1094: дублі після імпорту кількох баз 1С — лише тим, хто редагує довідник. */}
          {canEdit && (dupQ.data?.groups.length ?? 0) > 0 && (
            <button type="button" onClick={() => navigate('/contractors/duplicates')}
              title="Той самий контрагент двічі (однаковий код). Перегляньте і обʼєднайте."
              style={{
                background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
                border: '1px solid var(--badge-amber-bd)', fontWeight: 600,
              }}>
              ⚠ дублі ({dupQ.data!.groups.length})
            </button>
          )}
          <button className="primary" onClick={() => navigate('/contractors/new')}>
            + {t('contractor.new')}
          </button>
        </div>
      </div>
      {/* ч.691: фільтри — окремим рядком під назвою сторінки (канон). */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0 0.75rem' }}>
        <input
          type="search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as '' | ContractorRoleFilter)}
        >
          <option value="">Усі ролі</option>
          <option value="buyer">Покупець</option>
          <option value="supplier">Постачальник</option>
          <option value="non_resident">Нерезидент</option>
          <option value="other">Iнше</option>
        </select>
        <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
          <option value="">Усі групи</option>
          {(groupsQ.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>
      <InfiniteDocGrid<Contractor>
        datasource={datasource}
        columnDefs={cols}
        storageKey="contractors"
        headerExtra={(
          <a href="/refs/contractor-groups" style={{ fontSize: 13 }}
            onClick={(e) => { e.preventDefault(); navigate('/refs/contractor-groups'); }}>
            керувати групами
          </a>
        )}
        onRowDoubleClicked={(r) => navigate(`/contractors/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
